import classNames from 'classnames'
import React, { type ComponentType, MouseEvent } from 'react'

import { Alert, type AlertProps } from '../alert/alert.js'
import { Container } from '../container/container.js'
import { GridColumn } from '../grid/grid-column.js'
import { Grid } from '../grid/grid.js'
import { useCurrentBreakPoint } from '../helper/hooks/use-current-breakpoint.js'
import { BurgerIcon, CloseIcon, type IconProps } from '../icon/icon.js'
import { SamsungFlexLogo } from '../logo/logo.js'
import { Separator } from '../separator/separator.js'

export interface HeaderFlexProps {
    alert?: AlertProps
    items: HeaderFlexItemProps[]
    toggleLabel: string
    className?: string
    isOpen?: boolean
    url?: string
    onChange?: (isOpen: boolean) => void
}

export interface HeaderFlexItemProps {
    label: string
    className?: string
    onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void | Promise<void>
    isCurrent?: boolean
    url?: string
    type?: 'auth' | 'debug'
    hideInMobile?: boolean
    hideInDesktop?: boolean
    icon?: ComponentType<IconProps>
    children?: HeaderFlexItemChildProps[]
}

interface HeaderFlexItemChildProps {
    icon: ComponentType<IconProps>
    label: string
    url: string
}

export const HeaderFlex = (props: HeaderFlexProps): JSX.Element => {
    const { isOpen, onChange, toggleLabel, url } = props

    const bp = useCurrentBreakPoint()
    const isMobile = bp === 'sm' || bp === 'xs'

    const items = props.items.filter((item) => {
        if (item.hideInMobile && isMobile) {
            return false
        }

        if (item.hideInDesktop && !isMobile) {
            return false
        }

        return true
    })

    const className: string = classNames(
        'header-flex',
        {
            'is-open': isOpen,
        },
        props.className,
    )

    const handleChange = () => {
        if (onChange) {
            onChange(!isOpen)
        }
    }

    return (
        <>
            <header className={className}>
                {props.alert && <Alert className="header-flex__alert" {...props.alert} />}
                <div className="header-flex__main">
                    <Container className="header-flex__container">
                        <Grid align={['middle-xs', 'between-xs']} noWrap={true}>
                            <GridColumn width={['xs']}>
                                <SamsungFlexLogo className="header-flex__logo" url={url} />
                            </GridColumn>
                            <GridColumn width={['xs']}>
                                <button
                                    aria-controls="header-flex-nav-dropdown"
                                    aria-expanded={isOpen ? 'true' : 'false'}
                                    className="header-flex__toggle"
                                    onClick={handleChange}
                                    type="button"
                                >
                                    {isOpen ? (
                                        <CloseIcon className="header-flex__icon" />
                                    ) : (
                                        <BurgerIcon className="header-flex__icon" />
                                    )}
                                    <span className="header-flex__toggle-label">{toggleLabel}</span>
                                </button>
                            </GridColumn>
                        </Grid>
                    </Container>
                    <div className="header-flex__inner">
                        <nav className="header-flex__nav">
                            <ul className="header-flex__list">
                                {items.map((item: HeaderFlexItemProps, index: number) => (
                                    <li
                                        className={classNames(item.type && 'header-flex__list-item--type')}
                                        key={`header-flex-item-${index}`}
                                    >
                                        <HeaderFlexItem {...item} />
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>
            </header>
            {/*
                Render Alert again, outside/under the fixed-position element.
                This won't be visible, but pushes the rest of the content down exactly the same amount
                as the visible alert's height (which is dynamic due to text wrapping).
                Hacky, but it works.
            */}
            {props.alert && <Alert className="header-flex__alert" {...props.alert} />}
        </>
    )
}

const HeaderFlexItem = (props: HeaderFlexItemProps): JSX.Element => {
    const { label, onClick, isCurrent, url, type } = props

    const Element: 'a' | 'button' = url ? 'a' : 'button'

    const className: string = classNames(
        'header-flex__item',
        {
            'is-current': isCurrent,
            [`header-flex__item--type-${type}`]: type,
        },
        props.className,
    )

    return (
        <>
            <Element
                className={className}
                href={url}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={onClick}
            >
                {label}
                {props.icon && <props.icon className="header-flex__icon" />}
            </Element>
            {props.children && props.children.length > 0 && (
                <ul className="header-flex__item-children">
                    {props.children.map((child: HeaderFlexItemChildProps, index: number) => (
                        <li key={index}>
                            {index > 0 && <Separator variant="slim" />}
                            <HeaderFlexItemChild {...child} />
                        </li>
                    ))}
                </ul>
            )}
        </>
    )
}

const HeaderFlexItemChild = (props: HeaderFlexItemChildProps): JSX.Element => (
    <a className={classNames('header-flex__item-child')} href={props.url}>
        <props.icon className="header-flex__item-child-icon" />
        <span>{props.label}</span>
    </a>
)
